import { ID_MAP_KEYS, SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  filterIncludedItem,
  generateMap,
} from 'utilities/hooks/responseHandlers';
import { numericSort } from 'utilities/sort';
import type { SparkCategory } from 'types/entityTypes/SparkCategories';
import type { MarketplaceAccount } from 'types/MarketplaceAccount';
import type { SparkJoinedFavouriteResponse } from 'types/Spark/SparkFavourite';
import type { SparkImage } from 'types/Spark/SparkImages';
import type { SparkRating } from 'types/Spark/SparkRating';
import type { Spark } from 'types/Spark/Spark';
import type { JoinedSparks } from 'types/Spark/JoinedSpark';
import type { FavouriteList } from 'types/FavouriteList';

type GroupedFavourites = {
  spark: JoinedSparks[];
  favourite: FavouriteList;
};

const generateJoinedFavouriteSparks = (
  response: SparkJoinedFavouriteResponse | undefined
): Record<string, GroupedFavourites> | undefined => {
  const sparkFavourite = response?.data;
  const included = response?.included;
  const sparks = filterIncludedItem<Spark>(
    included,
    SPARK_RESOURCE_TYPES.SPARKS
  );
  const favouriteList = filterIncludedItem<FavouriteList>(
    included,
    SPARK_RESOURCE_TYPES.FAVOURITE_LIST
  );

  const marketplaceAccounts = filterIncludedItem<MarketplaceAccount>(
    included,
    SPARK_RESOURCE_TYPES.MARKETPLACE_ACCOUNTS
  );
  const sparkImages = filterIncludedItem<SparkImage>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_IMAGES
  );
  const sparkCategories = filterIncludedItem<SparkCategory>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_CATEGORIES
  );
  const sparkRatings = filterIncludedItem<SparkRating>(
    included,
    SPARK_RESOURCE_TYPES.SPARK_RATINGS
  );

  const joinedSparks: JoinedSparks[] | undefined = sparks?.map((spark) => {
    return {
      ...spark,
      sparkCategories: sparkCategories?.filter(
        (category) => category.attributes.sparkID === spark.id
      ),
      marketplaceAccount: marketplaceAccounts?.find(
        (marketplaceAccount) =>
          marketplaceAccount.id === spark.attributes.marketplaceAccountID
      ),
      sparkImages: sparkImages
        ?.filter((sparImage) => sparImage.attributes.sparkID === spark.id)
        .sort((a, b) =>
          numericSort(a.attributes.sortOrder, b.attributes.sortOrder)
        ),
      sparkRatings: sparkRatings?.filter(
        (sparkRating) => sparkRating.attributes.sparkID === spark.id
      ),
      sparkFavourites: sparkFavourite?.filter(
        (favourite) => favourite.attributes.sparkID === spark.id
      ),
    };
  });
  const favouriteListIDMap = generateMap<FavouriteList>(
    favouriteList,
    ID_MAP_KEYS.ID
  );
  const joinedSparksIDMap = generateMap<JoinedSparks>(
    joinedSparks,
    ID_MAP_KEYS.ID
  );

  return sparkFavourite?.reduce((acc, curr) => {
    const favID = curr.attributes.favouriteListID;
    const spark = joinedSparksIDMap?.[curr.attributes.sparkID];
    const favourite = favouriteListIDMap?.[favID];

    if (spark && favourite) {
      if (!acc[favID]) {
        acc[favID] = { favourite, spark: [] };
      }
      acc[favID].spark.push(spark);
    }
    return acc;
  }, {} as { [id: string]: GroupedFavourites });
};

export { generateJoinedFavouriteSparks };
